import { Link } from 'react-router-dom'
import { Info, Plug } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useConnections } from '@/hooks/useConnections'
import type { WizardData } from './types'

interface StepIntegrationProps {
  data: WizardData
  onChange: (data: WizardData) => void
}

export function StepIntegration({ data, onChange }: StepIntegrationProps) {
  const { data: connections, isLoading } = useConnections()
  const connected = connections?.filter((c) => c.status === 'connected') ?? []

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">App Connection</h3>
        <p className="text-sm text-muted-foreground">
          Select a connected app to dispatch service requests to when conversations complete.
        </p>
      </div>

      <div className="space-y-2 max-w-md">
        <Label>Connected App</Label>
        <Select
          value={data.app_connection_id != null ? String(data.app_connection_id) : 'none'}
          onValueChange={(v) =>
            onChange({ ...data, app_connection_id: v === 'none' ? null : parseInt(v) })
          }
          disabled={isLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder={isLoading ? 'Loading...' : 'Select a connection'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {connected.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                <span className="flex items-center gap-2">
                  <Plug className="h-3 w-3" />
                  {c.label} ({c.provider})
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {connected.length === 0 && !isLoading && (
        <Card className="bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-900">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              No apps connected yet.{' '}
              <Link to="/app/connections" className="underline font-medium">
                Set up a connection
              </Link>{' '}
              first, then come back to select it here.
            </p>
          </CardContent>
        </Card>
      )}

      {data.app_connection_id == null && (
        <Card className="bg-muted/50">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <Info className="h-5 w-5 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground">
              Without a connection, service requests will be logged internally but not dispatched to an external system.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
